import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';

import { formatPrice } from 'utilities/money';

import { GreenButton, PurpleButton, WhiteButton } from '@marketing/components/buttons';
import { OfferProps } from '@marketing/types';

import ArrowWithLoop from './assets/arrow_with_loop.svg'
import CheckmarkIcon from '../../assets/checkmark_icon.svg';
import styles from './style.module.scss';

interface Props {
  offers: OfferProps[];
}

export default (props: Props) => {
  const admissionsBundleOffer = props.offers.find((offer) => offer.slug === 'premium-admissions-bundle' || offer.slug === 'premium-admissions-bundle-in');
  const premiumOffer = props.offers.find((offer) => offer.slug === 'premium' || offer.slug === 'premium-in');
  const premium30DaysOffer = props.offers.find((offer) => offer.slug === 'premium-30-days' || offer.slug === 'premium-30-days-in');
  const trialOffer = props.offers.find((offer) => offer.slug === '1-week');

  const prepareSubtextLine = (subtextLine, offer) => {
    let line = subtextLine;

    line = line.replace(
      '{{original-price}}',
      formatPrice(offer.fullPriceInCents, 'usd', true)
    );

    line = line.replace(
      '{{new-price}}',
      formatPrice(offer.priceInCents, 'usd', true)
    );

    return line;
  };

  const Feature = (props) => (
    <div
      className="d-flex gap-xs"
      data-tooltip-id="feature-tooltip"
      data-tooltip-html={props.tooltip}
    >
      <img
        alt="Checkmark icon"
        height="16px"
        loading="lazy"
        src={CheckmarkIcon}
        style={{ height: 16, marginTop: 2, width: 14 }}
        width="14px"
      />

      <div className={cx(props.className, 'small text-inter')}>
        <span className={cx({ 'text-underdash': !!props.tooltip })}>
          {props.children}
        </span>
      </div>
    </div>
  );

  const OnSaleBadge = () => (
    <div
      className="align-items-center d-flex gap-xs"
      style={{
        background: 'linear-gradient(to bottom, rgb(253, 199, 66), rgb(252, 185, 20))',
        borderRadius: 4,
        fontSize: 11,
        padding: '4px 8px'
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none">
        <path fill="#C38E0B" d="M12.24 6.278 6.722.76A.881.881 0 0 0 6.094.5H.944A.444.444 0 0 0 .5.944v5.15a.881.881 0 0 0 .26.628l5.518 5.518a.89.89 0 0 0 1.257 0l4.705-4.705a.89.89 0 0 0 0-1.257ZM3.389 4.056a.667.667 0 1 1 0-1.334.667.667 0 0 1 0 1.334Z" />
      </svg>

      <strong>ON SALE</strong>
    </div>
  );

  return (
    <div className={styles.offersContainer}>
      {/* Offsetting the target to make room for the sticky navbar and notice bar */}
      <div id="offers" style={{ marginTop: -140, position: 'absolute' }} />

      <div className="hidden-xs hidden-sm text-inter text-right u-padding-B-m u-padding-R-m" style={{ color: '#472375' }}>
        <small>Bundle and save <strong>$854</strong> ($1073 value)</small>
      </div>

      <div
        className={cx(styles.pricingAndFeaturesContainer, 'd-flex flex-column gap-m')}
        style={{
          background: '#F7F7F7',
          border: '1px solid #DEDDDB',
          boxShadow: 'inset 0 2px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className={cx(styles.offersGrid, 'd-flex flex-column flex-lg-row gap-l gap-lg-m text-inter')}>
          <div className={cx(styles.offer, 'd-flex flex-column flex-1')}>
            <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#FFFFFF' }}>
              <div
                className="d-flex flex-column gap-xs"
                style={{ background: '#F7F7F7', borderRadius: 16, padding: 10 }}
              >
                <div style={{ fontSize: 18 }}>
                  <span className="text-bold text-gray-darker">Premium</span>
                  {' '}<span style={{ color: '#C6C6C6' }}>·</span>{' '}
                  <span className="text-bold text-gray-darker">1 month</span>
                </div>

                <div style={{ color: '#5C5C5C', fontSize: 13 }}>
                  Great option for limited study time
                </div>
              </div>

              <ExpandableContentAndFeatures
                className="d-flex flex-column flex-1 gap-m"
                triggerColor="#222123"
              >
                <div className={styles.offerContentAndFeatures}>
                  <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                    Content
                  </div>

                  <Feature tooltip="Content that you can't currently get anywhere else!">
                    <strong>8 full sections</strong> of official GRE® questions
                  </Feature>

                  <Feature tooltip="Lessons cover every concept tested on the GRE">
                    <strong>290+</strong> video lessons
                  </Feature>

                  <Feature tooltip="Every question has its own in-depth<br />explanation video">
                    <strong>Over 1600</strong> practice questions
                  </Feature>

                  <Feature tooltip="Our timed and scored full-length practice<br />tests are generated from your pool of 1600<br />practice questions. We recommend taking 6<br />tests with Magoosh, but you can take more.">
                    Up to <strong>6 practice tests</strong>
                  </Feature>

                  <Feature tooltip="Study schedules and checklists to keep you<br />on track">
                    Study schedules
                  </Feature>
                </div>

                <div className={styles.offerContentAndFeatures}>
                  <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                    Features
                  </div>

                  <Feature tooltip="Full access for 1 month">
                    <strong>1 month</strong> of access
                  </Feature>

                  <Feature tooltip="If you've taken the test before and meet<br />our requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund. See<br />the Frequently Asked Questions below for details.">
                    <strong>+5 total</strong> score guarantee
                  </Feature>

                  <Feature tooltip="Quick email and chat support for when you<br />get stuck or just want some extra help">
                    Ask an expert
                  </Feature>

                  <Feature tooltip="Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan’s end date will be<br />extended by the paused duration.">
                    Pause your plan
                  </Feature>
                </div>
              </ExpandableContentAndFeatures>

              <div className="d-flex flex-column gap-m justify-content-end">
                <div className="d-flex flex-column flex-1 gap-xs">
                  {
                    premium30DaysOffer.priceInCents !== premium30DaysOffer.fullPriceInCents && (
                      <div className="d-flex gap-s">
                        <OnSaleBadge />

                        <strong
                          className="text-medium text-gray-darker"
                          style={{ fontSize: 15, textDecoration: 'line-through' }}
                        >
                          Usually {formatPrice(premium30DaysOffer.fullPriceInCents, 'usd', true)}
                        </strong>
                      </div>
                    )
                  }

                  <div className="text-gray-darker" style={{ fontSize: 30 }}>
                    <strong>{formatPrice(premium30DaysOffer.priceInCents, 'usd', true)} USD</strong>
                  </div>

                  {
                    premium30DaysOffer.subtextLines.length !== 0 && (
                      <div
                        className="d-flex flex-column"
                        style={{
                          borderTop: '1px solid rgba(0, 0, 0, 0.07)',
                          marginTop: 10,
                          padding: '10px 0',
                          width: '100%'
                        }}
                      >
                        {
                          premium30DaysOffer.subtextLines.map((subtextLine, idx) => (
                            <div key={idx} style={{ color: '#5C5C5C', fontSize: 14 }}>
                              <strong>{prepareSubtextLine(subtextLine, premium30DaysOffer)}</strong>
                            </div>
                          ))
                        }
                      </div>
                    )
                  }

                {/* Placeholder to match height of monthly installments div on other plans */}
                  <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                </div>

                <WhiteButton href={premium30DaysOffer.registrationUrl}>Sign Up Now</WhiteButton>
              </div>
            </div>
          </div>

          <div className={cx(styles.offer, 'd-flex flex-column flex-1')}>
            <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#E9F8F2' }}>
              <div
                className="d-flex flex-column gap-xs"
                style={{ background: '#C2EBDB', borderRadius: 16, padding: 10 }}
              >
                <div style={{ fontSize: 18 }}>
                  <span className="text-bold text-gray-darker">Premium</span>
                  {' '}<span style={{ color: '#9BBCAF' }}>·</span>{' '}
                  <span className="text-bold text-gray-darker">6 months</span>
                </div>

                <div style={{ color: '#5C5C5C', fontSize: 13 }}>
                  Save $715 by purchasing 6 months at once
                </div>
              </div>

              <ExpandableContentAndFeatures
                className="d-flex flex-column flex-1 gap-m"
                triggerColor="#222123"
              >
                <div className={styles.offerContentAndFeatures}>
                  <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                    Content
                  </div>

                  <Feature tooltip="Content that you can't currently get anywhere else!">
                    <strong>8 full sections</strong> of official GRE® questions
                  </Feature>

                  <Feature tooltip="Lessons cover every concept tested on the GRE">
                    <strong>290+</strong> video lessons
                  </Feature>

                  <Feature tooltip="Every question has its own in-depth<br />explanation video">
                    <strong>Over 1600</strong> practice questions
                  </Feature>

                  <Feature tooltip="Our timed and scored full-length practice<br />tests are generated from your pool of 1600<br />practice questions. We recommend taking 6<br />tests with Magoosh, but you can take more.">
                    Up to <strong>6 practice tests</strong>
                  </Feature>

                  <Feature tooltip="Study schedules and checklists to keep you<br />on track">
                    Study schedules
                  </Feature>
                </div>

                <div className={styles.offerContentAndFeatures}>
                  <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                    Features
                  </div>

                  <Feature tooltip="Full access for 6 months">
                    <strong>6 months</strong> of access
                  </Feature>

                  <Feature tooltip="If you've taken the test before and meet<br />our requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund. See<br />the Frequently Asked Questions below for details.">
                    <strong>+5 total</strong> score guarantee
                  </Feature>

                  <Feature tooltip="Quick email and chat support for when you<br />get stuck or just want some extra help">
                    Ask an expert
                  </Feature>

                  <Feature tooltip="Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan’s end date will be<br />extended by the paused duration.">
                    Pause your plan
                  </Feature>
                </div>
              </ExpandableContentAndFeatures>

              <div className="d-flex flex-column gap-m justify-content-end">
                <div className="d-flex flex-column flex-1 gap-xs">
                  {
                    premiumOffer.priceInCents !== premiumOffer.fullPriceInCents && (
                      <div className="d-flex gap-s">
                        <OnSaleBadge />

                        <strong
                          className="text-medium text-gray-darker"
                          style={{ fontSize: 15, textDecoration: 'line-through' }}
                        >
                          Usually {formatPrice(premiumOffer.fullPriceInCents, 'usd', true)}
                        </strong>
                      </div>
                    )
                  }

                  <div className="text-gray-darker" style={{ fontSize: 30 }}>
                    <strong>{formatPrice(premiumOffer.priceInCents, 'usd', true)} USD</strong>
                  </div>

                  {
                    premiumOffer.subtextLines.length !== 0 && (
                      <div
                        className="d-flex flex-column"
                        style={{
                          borderTop: '1px solid rgba(0, 0, 0, 0.07)',
                          marginTop: 10,
                          padding: '10px 0',
                          width: '100%'
                        }}
                      >
                        {
                          premiumOffer.subtextLines.map((subtextLine, idx) => (
                            <div key={idx} style={{ color: '#5C5C5C', fontSize: 14 }}>
                              <strong>{prepareSubtextLine(subtextLine, premiumOffer)}</strong>
                            </div>
                          ))
                        }
                      </div>
                    )
                  }

                  <div style={{ color: '#5C5C5C', fontSize: 14, height: 20 }}>
                    Or 4 monthly payments of {formatPrice(Math.round(premiumOffer.priceInCents / 4))}
                  </div>
                </div>

                <GreenButton className="text-gray-darker" href={premiumOffer.registrationUrl}>
                  Sign Up Now
                </GreenButton>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column flex-1">
            <div className="text-inter text-center u-padding-B-m visible-xs visible-sm" style={{ color: '#472375' }}>
              <small>Bundle and save <strong>$854</strong> ($1073 value)</small>
            </div>

            <div className={cx(styles.offer, 'd-flex flex-column flex-1')} style={{ position: 'relative' }}>
              <div className={styles.arrowWithLoop}>
                <img alt="" src={ArrowWithLoop} />
              </div>

              <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#472375' }}>
                <div
                  className="d-flex flex-column gap-xs"
                  style={{ background: '#5D3391', borderRadius: 16, padding: 10 }}
                >
                  <div style={{ fontSize: 18 }}>
                    <span className="text-bold text-white">Premium</span>
                    {' '}<span style={{ color: '#8E70B2' }}>+</span>{' '}
                    <span className="text-bold text-white">Admissions</span>
                  </div>

                  <div style={{ color: '#DDB2FF', fontSize: 13 }}>
                    Comprehensive application support
                  </div>
                </div>

                <ExpandableContentAndFeatures
                  className="d-flex flex-column flex-1 gap-m"
                  triggerColor="#FFFFFF"
                >
                  <div className={styles.offerContentAndFeatures}>
                    <div className="text-medium text-upper" style={{ color: '#DDB2FF', fontSize: 12 }}>
                      Premium GRE ($894 value)
                    </div>

                    <Feature className="text-white">
                      6 months access to everything in our Premium GRE plan
                    </Feature>
                  </div>

                  <div className={styles.offerContentAndFeatures}>
                    <div className="text-medium text-upper" style={{ color: '#DDB2FF', fontSize: 12 }}>
                      Admissions Support ($179 value)
                    </div>

                    <div style={{ color: '#DDB2FF', fontSize: 14 }}>
                      Demystify graduate applications with people who have been there
                    </div>

                    <Feature className="text-white" tooltip="Manage your grad school applications<br />in one central place.">
                      Application tracker
                    </Feature>

                    <Feature className="text-white" tooltip="Go from blank page to well-written draft<br />with our Guided Writing experience.">
                      Step-by-step guided essay writing
                    </Feature>

                    <Feature className="text-white" tooltip="Learn how to put your best foot forward<br />from a top lineup of Magoosh admissions experts<br />with a range of diverse backgrounds.">
                      50+ video workshops
                    </Feature>

                    <Feature className="text-white" tooltip="Check out actual student writings<br />annotated with expert feedback to help you<br />tell your story in the best light">
                      40+ annotated SOPs and Personal Statements
                    </Feature>

                    <Feature className="text-white" tooltip="Full access to Magoosh Admissions for 12 months.">
                      12 months of access
                    </Feature>
                  </div>
                </ExpandableContentAndFeatures>

                <div className="d-flex flex-column gap-m justify-content-end">
                  <div className="d-flex flex-column flex-1 gap-xs">
                    {
                      admissionsBundleOffer.priceInCents !== admissionsBundleOffer.fullPriceInCents && (
                        <div className="d-flex gap-s">
                          <OnSaleBadge />

                          <strong
                            className="text-medium text-white"
                            style={{ fontSize: 15, textDecoration: 'line-through' }}
                          >
                            Usually {formatPrice(admissionsBundleOffer.fullPriceInCents, 'usd', true)}
                          </strong>
                        </div>
                      )
                    }
                    <div className="d-flex gap-s text-white" style={{ fontSize: 30 }}>
                      <strong>{formatPrice(admissionsBundleOffer.priceInCents, 'usd', true)} USD</strong>
                    </div>

                    {
                      admissionsBundleOffer.subtextLines.length !== 0 && (
                        <div
                          className="d-flex flex-column"
                          style={{
                            borderTop: '1px solid #DDB2FF',
                            marginTop: 10,
                            padding: '10px 0',
                            width: '100%'
                          }}
                        >
                          {
                            admissionsBundleOffer.subtextLines.map((subtextLine, idx) => (
                              <div key={idx} style={{ color: '#FFFFFF', fontSize: 14 }}>
                                <strong>{prepareSubtextLine(subtextLine, admissionsBundleOffer)}</strong>
                              </div>
                            ))
                          }
                        </div>
                      )
                    }

                    <div style={{ color: '#DDB2FF', fontSize: 14, height: 20 }}>
                      Or 4 monthly payments of {formatPrice(Math.round(admissionsBundleOffer.priceInCents / 4))}
                    </div>
                  </div>

                  <PurpleButton className="text-gray-darker" href={admissionsBundleOffer.registrationUrl}>
                    Sign Up Now
                  </PurpleButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="align-items-center d-flex flex-column gap-s u-margin-V-m">
          <div className="align-items-center d-flex flex-column text-inter" style={{ color: '#5C5C5C' }}>
            <div className="text-center"><strong>Try Magoosh GRE for 7 days</strong></div>
            <div className="text-center">Start a free trial with 20+ questions and lessons</div>
          </div>

          <div>
            <WhiteButton href={trialOffer.registrationUrl} size="sm">
              Start a Free Trial
            </WhiteButton>
          </div>
        </div>
      </div>

      <Tooltip className="text-center" id="feature-tooltip" />
    </div>
  );
};


const ExpandableContentAndFeatures: React.FC<{ children: any; className?: string; triggerColor: string }> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const determineContentHeight = useRef(() => {
    if (ref.current) {
      setContentHeight(ref.current.clientHeight);
    }
  });

  useEffect(() => {
    determineContentHeight.current();

    window.addEventListener('resize', determineContentHeight.current);

    return () => {
      window.removeEventListener('resize', determineContentHeight.current);
    };
  }, [props.children]);

  return (
    <>
      <div className="visible-xs visible-sm u-margin-B-s">
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <span className="text-inter" style={{ color: props.triggerColor, fontSize: 14 }}>
            <strong>See plan details</strong>
          </span>

          <span style={{ paddingLeft: 5, transform: isExpanded ? 'rotate(180deg)': '' }}>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.75 1L4.0429 4.2929C4.4334 4.6834 5.0666 4.6834 5.4571 4.2929L8.75 1" stroke={props.triggerColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>

        <div
          style={{
            height: isExpanded ? contentHeight : 0,
            overflow: 'hidden',
            transition: 'height 200ms'
          }}
        >
          <div className={props.className} ref={ref}>
            {props.children}
          </div>
        </div>
      </div>

      <div className="flex-1 hidden-xs hidden-sm">
        <div className={props.className}>
          {props.children}
        </div>
      </div>
    </>
  );
};
